import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { Card, Table } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { Company, Goal, PageInfo, Period } from '../../models';
import { ActiveDotCell } from '../shared';
import { formatDateStringToISO } from '../../lib/utils';
import { findItemByText } from '../../lib';
import { useAlgoliaSearch, useUser } from '../../hooks';

const columnHelper = createColumnHelper<Goal>();

const columns = [
  columnHelper.accessor('metric', {
    header: 'Métrica',
  }),
  columnHelper.accessor('target', {
    header: 'Meta de período',
    cell: (info) => {
      const period = info.row.original.periods.find(
        (periodItem) => periodItem.current
      );
      return period ? period.target : 'N/A';
    },
  }),
  columnHelper.accessor('employeeCount', {
    header: 'N° de beneficiarios',
    cell: (info) => `0`,
  }),
  columnHelper.accessor('periodsCount', {
    header: 'N° de períodos',
    cell: (info) => `${info.row.original.periods.length}`,
  }),
  columnHelper.accessor('actualPeriod', {
    header: 'Período actual',
    cell: (info) => {
      const { periods } = info.row.original;
      let activePeriod: Period;

      // Iteramos sobre todos los períodos
      periods.forEach((period) => {
        const startDate = new Date(period.startDate);
        const endDate = new Date(period.endDate);
        const today = new Date();

        // Si el período actual está dentro del rango, lo almacenamos y salimos del bucle
        if (today >= startDate && today <= endDate) {
          activePeriod = period;
        }
      });

      // Si se encontró un período activo, retornamos sus fechas formateadas
      if (activePeriod) {
        const formattedStartDate = formatDateStringToISO(
          activePeriod.startDate
        );
        const formattedEndDate = formatDateStringToISO(activePeriod.endDate);
        return `${formattedStartDate} - ${formattedEndDate}`;
      }
      return <ActiveDotCell text="Sin período activo" active={false} />;
    },
  }),
];

interface GoalTableProps {
  data: Goal[];
  refetch: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      company: Company;
    }>
  >;
}

function GoalTable({ data, refetch }: GoalTableProps) {
  const router = useRouter();
  const [user] = useUser();
  const [goalsData, setGoalsData] = React.useState(data);
  const [searchText, setSearchText] = React.useState<string | number>('');
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/goals/${id}`);
  };

  const onClickAddButton = () => router.push('/app/goals/create');

  const { fetchData } = useAlgoliaSearch<Goal>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: ['companyDetails.goals.metric'],
    index: 'companies',
    filters: `id:${(user.company as Company)?._id}`,
  });

  const memoizedSyncData = React.useCallback(async () => {
    if (searchText) {
      const { pageInfo, items } = await fetchData({
        searchText: searchText.toString(),
        newPage: pageInfoAlgolia.currentPage,
      });
      const goals = (items as Company)[0]?.companyDetails?.goals ?? [];
      const filterGoals = findItemByText(
        goals,
        searchText.toString()
      ) as Hit<Goal>[];
      setGoalsData(filterGoals);
      setPageInfoAlgolia(pageInfo);
    } else {
      // Llama a refetch y espera su resultado
      const { data: refetchedData } = await refetch();

      if (refetchedData) {
        setGoalsData(refetchedData.company?.companyDetails?.goals ?? []);
      }
    }
  }, [searchText, pageInfoAlgolia?.currentPage, refetch]);

  React.useEffect(() => {
    memoizedSyncData();
  }, [searchText, memoizedSyncData]);

  return (
    <Card className="overflow-x-auto p-5">
      <Table<Goal> data={goalsData} columns={columns} href="/app/goals/create">
        <Table.Header>
          <Table.SearchInput
            placeholder="Buscar por métrica"
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Meta" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<Goal> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination className="ml-auto" />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default GoalTable;
